'use client';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Head from 'next/head';
import Link from 'next/link'; // keep for any internal links
import ChatPopup from '../../src/components/home/ChatPopup';
import Slider from '../../src/components/slider/Slider';
import FaqsStylistCard from '../../src/components/StylistDescriptionCard/FaqsStylistCard';
import { listCoupon } from '../../src/actions/couponActions';
import { listFaqs } from '../../src/actions/faqsAction';
import { FaqsData } from '../../src/fakeData/Faqs';
import { seoData } from '../../src/fakeData/seoData';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

function FAQButton({ question, answer, isOpen, onClick }) {
  return (
    <div className="mt-2">
      <button
        className={`flex justify-between w-full px-4 py-3 text-left focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75 text-gray-600 bg-gray-50 hover:bg-emerald-50 rounded-lg text-base md:text-lg lg:text-base`}
        type="button"
        onClick={onClick}
      >
        <span>{question}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
          className={`w-5 h-5 text-gray-500 transition-transform transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <path
            fillRule="evenodd"
            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0
            11-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010
            1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="mt-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function Faqs() {
  const dispatch = useDispatch();
  const coupons = useSelector((state) => state.couponList.coupons) || {};
  const faqsQA = useSelector((state) => state.FaqsQAList.faqsQA) || [];
  const language = useSelector((state) => state.language.language);

  const [openIndex, setOpenIndex] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [canonicalUrl, setCanonicalUrl] = useState('');

  const text = {
    cardEmail: language === 'en' ? 'Email Us ' : 'हमें ईमेल करें',
    cardCall: language === 'en' ? 'Call Us ' : 'हमें कॉल करें',
    cardLocation: language === 'en' ? 'Location ' : 'स्थान',
    cardLocationDescription:
      language === 'en'
        ? 'Block A4, Sector 16, Noida, Uttar Pradesh 201301'
        : 'ब्लॉक ए4, सेक्टर 16, नोएडा, उत्तर प्रदेश 201301',
  };

  useEffect(() => {
    dispatch(listCoupon());
    dispatch(listFaqs());
    window.scrollTo(0, 0);

    setCanonicalUrl(window.location.href);

    const onScroll = () => setShowScrollButton(window.scrollY > 100);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [dispatch]);

  const toggleFAQ = (i) => setOpenIndex(openIndex === i ? null : i);

  const { title, description, keywords, author } = seoData.faqs;

  const twoCoupons = coupons.result ? coupons.result.slice(0, 2) : [];

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content={author} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Head>

      <div className="bg-gray-50">
        <div className="bg-white">
          <ChatPopup />
          <div className="mx-auto py-5 max-w-screen-2xl px-3 sm:px-10">
            <Slider />
          </div>

          <div className="max-w-screen-2xl mx-auto lg:py-20 py-10 px-4 sm:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
            <div className="border p-10 rounded-lg text-center">
              <span className="flex justify-center text-4xl text-pink-600 mb-4">
                {/* Icon here */}
              </span>
              <h5 className="text-xl mb-2 font-bold">{text.cardEmail}</h5>
              <p className="mb-0 text-base opacity-90 leading-7">
                <a
                  href="mailto:doortodoorbeauty1@gmail.com"
                  className="text-pink-600"
                >
                  doortodoorbeauty1@gmail.com
                </a>
              </p>
            </div>
            <div className="border p-10 rounded-lg text-center">
              <span className="flex justify-center text-4xl text-pink-600 mb-4">
                {/* Icon */}
              </span>
              <h5 className="text-xl mb-2 font-bold">{text.cardCall}</h5>
              <p className="mb-0 text-base opacity-90 leading-7">
                <a href="tel:7289859266" className="text-pink-600">
                  +91‑72898 59266
                </a>
              </p>
            </div>
            <div className="border p-10 rounded-lg text-center">
              <span className="flex justify-center text-4xl text-pink-600 mb-4">
                {/* Icon */}
              </span>
              <h5 className="text-xl mb-2 font-bold">{text.cardLocation}</h5>
              <p className="mb-0 text-base opacity-90 leading-7">
                {text.cardLocationDescription}
              </p>
            </div>
          </div>

          <div className="bg-white -mt-16">
            <div className="max-w-screen-2xl mx-auto px-3 sm:px-10 py-10 lg:py-12 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:mb-8 items-center">
              <div className="hidden lg:block pr-16">
                <img alt="faq illustration" loading="lazy" src='/assets/Faqs/faqsSideimage.svg' />
              </div>
              <div>
                {FaqsData.map((item, idx) => (
                  <FAQButton
                    key={idx}
                    question={language === 'en' ? item.question : item.question_hindi}
                    answer={language === 'en' ? item.answer : item.answer_hindi}
                    isOpen={openIndex === idx}
                    onClick={() => toggleFAQ(idx)}
                  />
                ))}
              </div>
            </div>
          </div>

          <FaqsStylistCard />

          {showScrollButton && (
            <button
              className="fixed bottom-[100px] right-8 bg-pink-600 hover:bg-emerald-600 text-white p-3 rounded-full shadow-lg"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              ⬆️
            </button>
          )}
        </div>
      </div>
    </>
  );
}
